import React, { useState, useRef, useEffect } from 'react';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { tA11y } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { STUDENT_APP_BUTTON_SIZES } from '@monorepo/shared/src/types';
import IconButton from 'components/common/Buttons/IconButton';
import clsx from 'clsx';

export type CarouselProps = {
  className?: any; // concatinated onto to carouselInnerContainer
  itemCount?: number;
  firstAssignmentId?: number;
};

export enum BUTTON_DIRECTIONS {
  NEXT = 'next',
  PREVOUS = 'previous',
}

const Carousel: React.FC<CarouselProps> = ({ children, className, firstAssignmentId }) => {
  const classes = styles();

  const outerContainerRef = useRef<HTMLDivElement>(null);
  const [xScrollPosition, setXScrollPosition] = useState<number>(0);

  const isMaxXScrollPosition =
    outerContainerRef?.current &&
    xScrollPosition + document.body.clientWidth >= outerContainerRef?.current?.scrollWidth;

  const scroll = (direction: string) => {
    if (outerContainerRef && outerContainerRef.current) {
      if (
        direction === BUTTON_DIRECTIONS.NEXT &&
        xScrollPosition < outerContainerRef?.current?.scrollWidth
      ) {
        scrollToXPosition(
          isMaxXScrollPosition ? xScrollPosition : xScrollPosition + document.body.clientWidth - 100
        );
      }

      if (direction === BUTTON_DIRECTIONS.PREVOUS && xScrollPosition > 0) {
        scrollToXPosition(
          xScrollPosition - document.body.clientWidth - document.body.clientWidth / 25
        );
      }
    }
  };

  const scrollToXPosition = (scrollPosition: number) => {
    outerContainerRef?.current?.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
    setXScrollPosition(scrollPosition);
  };

  useEffect(() => {
    if (firstAssignmentId) {
      const firstAssignmentPosition =
        document.getElementById(`${firstAssignmentId}`)?.getBoundingClientRect()?.left ?? 0;
      scrollToXPosition(firstAssignmentPosition);
    }
  }, [firstAssignmentId]);

  return (
    <div className={classes.carouselButtonContainer}>
      <div className={classes.carouselOuterContainer} ref={outerContainerRef}>
        <div className={clsx(classes.carouselInnerContainer, className)}>{children}</div>
      </div>
      <IconButton
        icon={faChevronLeft}
        className={classes.carouselNavButton}
        style={{ left: 0 }}
        disabled={xScrollPosition <= 0}
        onClick={() => scroll(BUTTON_DIRECTIONS.PREVOUS)}
        aria-label={tA11y('buttons.previousButton')}
        color="inherit"
      />
      <IconButton
        icon={faChevronRight}
        className={classes.carouselNavButton}
        style={{ right: 0 }}
        disabled={isMaxXScrollPosition ?? false}
        onClick={() => scroll(BUTTON_DIRECTIONS.NEXT)}
        aria-label={tA11y('buttons.nextButton')}
        color="inherit"
      />
    </div>
  );
};

const styles = makeStyles((theme) => ({
  carouselNavButton: {
    position: 'absolute',
    bottom: '50%',
    transform: 'translateY(50%)',
    margin: '1rem',
    zIndex: 2,
  },
  carouselOuterContainer: {
    height: '100%',
    overflow: 'scroll hidden',
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  carouselInnerContainer: {
    width: `max-content`,
    padding: `0 ${remCalc(STUDENT_APP_BUTTON_SIZES.default + theme.constants.app.paddingX)}`,
    '& > *': {
      marginRight: theme.typography.pxToRem(24),
    },
  },
  carouselButtonContainer: {
    height: '100%',
    position: 'relative',
  },
}));

export default Carousel;
